import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ShareBoardModal from '../../components/Interactables/Modals/ShareBoardModal';
import SettingBoardModal from '../../components/Interactables/Modals/SettingBoardModal';
import Sidebar from '../../components/Interactables/Sidebar';
import SidebarToggle from '../../components/Interactables/Sidebar/SidebarToggle';
import ComponentManager from '../../components/BoardComponents/ComponentManager';
import { useAuth } from '../../context/AuthContext';
import api from '../../config/api-config';
import './Board.css';

function Board() {
  const { boardId } = useParams();
  const { user } = useAuth();
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isSettingModalOpen, setIsSettingModalOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const navigate = useNavigate();
  
  // Reference to the ComponentManager - must be defined before any conditional returns
  const componentManagerRef = useRef(null);

  useEffect(() => {
    const fetchBoard = async () => {
      if (!user || !boardId) return;
      
      try {
        setLoading(true);
        
        // Check if user has getIdToken method
        if (typeof user.getIdToken !== 'function') {
          console.error('Error fetching board: user.getIdToken is not a function');
          setError('Authentication error. Please try logging in again.');
          setLoading(false);
          return;
        }
        
        const token = await user.getIdToken();
        const response = await api.get(`/api/boards/${boardId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        });
        setBoard(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching board:', err);
        setError('Failed to load board. You may not have permission to view this board.');
      } finally {
        setLoading(false);
      }
    };

    fetchBoard();
  }, [boardId, user]);

  const handleBackToDashboard = () => {
    navigate('/');
  };

  if (loading) {
    return <div className="board-container loading">Loading board...</div>;
  }

  if (error) {
    return (
      <div className="board-container error">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={handleBackToDashboard}>Back to Dashboard</button>
      </div>
    );
  }

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Function to add a component via the sidebar
  const handleAddComponent = (type) => {
    if (componentManagerRef.current) {
      componentManagerRef.current.addComponent(type);
    }
  };

  return (
    <div className="board-container">
      <Sidebar 
        isOpen={isSidebarOpen} 
        onClose={toggleSidebar} 
        onAddComponent={handleAddComponent}
        onOpenSettings={() => setIsSettingModalOpen(true)}
        onOpenShare={() => setIsShareModalOpen(true)}
      />
      
      {isSidebarOpen && (
        <div className="sidebar-overlay visible" onClick={toggleSidebar}></div>
      )}
      
      <div className="board-header">
        <div className="board-title">
          <button className="back-button" onClick={handleBackToDashboard}>
            &larr; Back
          </button>
          <h1>{board?.name || 'Untitled Board'}</h1>
        </div>
        <div className="board-actions">
          <SidebarToggle isOpen={isSidebarOpen} onClick={toggleSidebar} />
        </div>
      </div>
      
      <div className="board-content">
        <ComponentManager 
          ref={componentManagerRef}
          boardId={boardId} 
        />
      </div>
      
      {isShareModalOpen && (
        <ShareBoardModal 
          boardId={boardId}
          onClose={() => setIsShareModalOpen(false)}
          onSuccess={() => {
            // You could refresh the board data here if needed
            setIsShareModalOpen(false);
          }}
        />
      )}

      {isSettingModalOpen && (
        <SettingBoardModal 
          boardId={boardId}
          onClose={() => setIsSettingModalOpen(false)}
          onSuccess={() => {
            // You could refresh the board data here if needed
            setIsSettingModalOpen(false);
          }}
        />
      )}
    </div>
  );
}

export default Board;
